import { AfterViewInit, Component, EventEmitter, Output, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GlobalSearchService } from '../../../../../../services/global-search.service';
import { GlobalSearchResultDto } from '../../../../../../models/api/globalSearchResultDto';
import { GlobalSearchRequest } from '../../../../../../models/api/globalSearchRequest';
import { EntityType } from '../../../../../../models/enums/entityType.enum';
import { Helpers } from '../../../../../../utilities/helpers';
import { TextNormalizeDirective } from '../../../../../../directives/text-normalize.directive';

@Component({
  selector: 'app-add-shareholder-search',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatInputModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatCheckboxModule,
    NgxSpinnerModule,
    FormsModule,
    TextNormalizeDirective
  ],
  templateUrl: './add-shareholder-search.component.html',
  styleUrls: ['./add-shareholder-search.component.scss']
})
export class AddShareholderSearchComponent implements AfterViewInit {
  @Output() shareholderSelected = new EventEmitter<GlobalSearchResultDto | null>();
  @Output() addNewRequested = new EventEmitter<void>();
  @Output() editRequested = new EventEmitter<GlobalSearchResultDto>();

  private globalSearchService = inject(GlobalSearchService);
  private spinnerService = inject(NgxSpinnerService);
  private router = inject(Router);

  Helpers = Helpers;
  EntityType = EntityType;

  dataSource = new MatTableDataSource<GlobalSearchResultDto>([]);
  private allColumns: string[] = ['actions', 'matchedYearsIcon', 'code', 'name', 'entityType', 'extraData'];
  pageSize: number = 5;
  showExtraData = false;

  get displayedColumns(): string[] {
    if (this.showExtraData) {
      return this.allColumns;
    }
    return this.allColumns.filter(c => c !== 'extraData');
  }
  selectedItem: GlobalSearchResultDto | null = null;
  searchTerm: string = '';
  hasSearched = false;

  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  get hasSearchCriteria(): boolean {
    return !!this.searchTerm?.trim();
  }

  onSearch(): void {
    if (!this.searchTerm?.trim()) return;

    const request: GlobalSearchRequest = {
      searchTerm: this.searchTerm.trim(),
      pageNumber: 1,
      pageSize: 100,
      sortBy: 'Name',
      ascending: true,
      entityTypes: [
        EntityType.LargeShareholder,
        EntityType.PyramidalShareholder,
        EntityType.Company,
        EntityType.OpbCompany,
        EntityType.Director,
      ]
    };

    this.spinnerService.show();
    this.globalSearchService.search(request).subscribe({
      next: (response) => {
        this.hasSearched = true;
        this.dataSource.data = response.data.map(r => {
          if (r.matchedYears?.length && (r.year == null || !r.matchedYears.includes(r.year))) {
            return { ...r, matchedYearsTooltip: `Matched in ${r.matchedYears.join(', ')}` };
          }
          return r;
        });
        if (this.paginator) {
          this.paginator.firstPage();
        }
        this.selectedItem = null;
        this.shareholderSelected.emit(null);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error searching:', error);
        this.spinnerService.hide();
      }
    });
  }

  onClear(): void {
    this.searchTerm = '';
    this.dataSource.data = [];
    this.selectedItem = null;
    this.hasSearched = false;
    this.shareholderSelected.emit(null);
  }

  onConnect(item: GlobalSearchResultDto): void {
    if (this.selectedItem?.id === item.id && this.selectedItem?.entityType === item.entityType) {
      this.selectedItem = null;
    } else {
      this.selectedItem = item;
    }
    this.shareholderSelected.emit(this.selectedItem);
  }

  isSelected(item: GlobalSearchResultDto): boolean {
    return this.selectedItem?.id === item.id && this.selectedItem?.entityType === item.entityType;
  }

  isEditable(item: GlobalSearchResultDto): boolean {
    return item.entityType === EntityType.LargeShareholder || item.entityType === EntityType.PyramidalShareholder;
  }

  /**
   * Directors are the results most often confused with one another — same or similar names, several
   * companies each — so their record can be opened alongside the dialog to check the CV before
   * connecting.
   */
  canOpenInNewTab(item: GlobalSearchResultDto): boolean {
    return item.entityType === EntityType.Director;
  }

  onOpenInNewTab(item: GlobalSearchResultDto, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canOpenInNewTab(item)) return;

    const url = this.router.serializeUrl(this.router.createUrlTree(['/directors/edit', item.id]));
    window.open(url, '_blank');
  }

  getExtraDataItems(item: GlobalSearchResultDto): { label: string }[] {
    if (!item.extraData || !item.extraData.length) return [];

    return (item.extraData as any[])
      .sort((a, b) => (a.year ?? 0) - (b.year ?? 0) || (this.getExtraDataName(a) ?? '').localeCompare(this.getExtraDataName(b) ?? ''))
      .map(d => {
        const name = this.getExtraDataName(d);
        const pct = d.firstClassSharesPercentage;
        return { label: `${name} (${d.year})${pct != null ? ' - ' + pct + '%' : ''}` };
      });
  }

  private getExtraDataName(d: any): string {
    return d.companyName ?? d.largeShareholderName ?? '';
  }

}
